import imageType from './assets/imageType.svg';
import videoType from './assets/videoType.svg';
import otherType from './assets/otherType.svg';
import pdfType from './assets/pdfType.svg';
import wordType from './assets/wordType.svg';
// eslint-disable-next-line
import FileStore from './store';
import { ReactNode } from 'react';
import { FileType, UploadFile } from '../business/TTCX/AssetManagement/common/type';

export interface FileUploadActionSchema {
  onSelectFile?: (fileList: FileList) => boolean; // when user select a file
  onBeforeDelete?: (aimFile: UploadTaskFileMeta) => Promise<boolean>;
  onUploadChange?: (
    aimFile: UploadTaskFileMeta,
    fileList: UploadTaskFileMeta[],
    hasUploadingTask: boolean
  ) => void; // output need to upload file list before starting upload
  // onUploadsSettled?: (fileList: UploadTaskFileMeta[]) => void; // output all file info when upload enter final status(success or fail)
  onError?: (aimFile: UploadTaskFileMeta, fileList: UploadTaskFileMeta[], message?: string) => void; // support output error message
  onSelectChange?: (selected: boolean, file: UploadTaskFileMeta) => void; // output selected file list
  onValidateBeforeUpload?: (fileList: FileList) => boolean; // custom validate
}

export interface FileUploadSchema {
  multiple?: boolean; // support multiple upload
  deDuplication?: boolean; // support de-duplication
  maxCount?: number; // support max supported count
  showUploadingCount?: boolean; // show max supported upload count
  showUploadButton?: boolean; // support upload file feature
  defaultFileList?: UploadTaskFileMeta[]; // initial file list
  showRemainSizeInfo?: boolean;
  showFileInfo?: boolean; // show file info when file is uploading
  defaultSelectvalue?: boolean; // default select value (true / false)
  showSelect?: boolean; // support select uploaded file
  showDelete?: boolean; // support delete uploaded file
  showPreview?: boolean; // support preview uploaded file fullscreen
  showDownload?: boolean; // support download uploaded file
  showVideoDuration?: boolean; // show uploaded video duration
  showReport?: boolean; // show report video
  customUploadingInfoRender?: (file: File) => React.ReactNode; // support user custom uploading infomation
  customProgressRender?: (progress: number) => React.ReactNode; // support user custom upload progress component
  customPreviewCard?: (fileInfo: UploadTaskFileMeta) => React.ReactNode; // support user custom preview uploaded file card
  customPreviewInfo?: (fileInfo: UploadTaskFileMeta) => React.ReactNode; // support user custom preview uploaded file info
  appName?: string; // application name
  appId?: number; // application id
  defaultImage?: string; // image / video default image url
  modifyTimeKey?: string;
  isTTCX?: boolean;
}

export interface FileValidateMainSchema {
  accept?: string; // support sccept file type
  singleMaxSize?: number; // support max size of single file
  totalMaxSize?: number; // support max size of single batch upload
  minSize?: number; // support min size of single batch upload
  pictureWidth?: number; // support upload image width
  pictureHeight?: number; // support upload image height
  disabled?: boolean;
  disabledTips?: string;
}

export enum UploadStatus {
  'uploading' = 1,
  'successed' = 2,
  'failed' = 3
}

export interface UploadTaskFileMeta extends SuccessDataSchema {
  file?: File;
  status?: UploadStatus;
  taskId?: string;
  store?: FileStore;
  cover?: string;
  duration?: number;
  vid?: string;
  videoPlayUrl?: string;
  videoOriginalUrl?: string;
  sparkAdsCode?: string;
  itemId?: string;
  videoLink?: string;
  assetFileId?: string;
}

export interface FileReportSchema {
  showReport?: boolean;
  onReport?: (vid: string) => void;
}

export interface UploadButtonSchema<T>
  extends FileUploadSchema,
    FileValidateMainSchema,
    FileUploadActionSchema,
    MonitorParamsSchema,
    FileReportSchema {
  canCancelUpload?: boolean; // can cancel upload in uploading progress
  useDrag?: boolean; // support drag upload
  uploadsServiceRegion?: 'sg' | 'us'; // upload service region
  containerCls?: string; // upload and preview component wrapper classname
  singleFileWrapperCls?: string; // upload and preview component wrapper classname
  wrapperStyle?: React.CSSProperties; // upload and preview component wrapper style
  dragWrapperCls?: string; // drag upload component classname
  width?: string; // preview component width
  height?: string; // preview component height
  buttonWrapperStyle?: React.CSSProperties; // upload button wrapper component style
  buttonClassName?: string; // upload button wrapper component classname
  buttonStyle?: React.CSSProperties;
  buttonIcon?: React.ReactNode; // upload button component icon
  dragIcon?: React.ReactNode; // drag component icon
  dragText?: string; // drag component button text
  tips?: string; // upload tips info
  errorMsgCom?: React.ReactNode;
  text?: string; // upload button content text
  isInternal?: boolean; // determine whether the validation goes to tt4b or internal sso
  slots?: {
    // support preview component slots
    bottom?: React.ReactNode;
    card?: React.ReactNode;
    renderBottom?: (fileInfo: UploadTaskFileMeta) => React.ReactNode;
    renderCard?: (fileInfo: UploadTaskFileMeta) => React.ReactNode;
  };
  extra?: T;
  customUploadButton?: ReactNode;
}

export interface SuccessDataSchema {
  source?: string; // vid / url
  cover?: string; // video cover
  name?: string;
  modifyTime?: number; // update time
  duration?: number; // video duration
  type?: string;
  videoInfoList?: any;
  cdnPath?: string; // looking at source property for video and looking at the cdnPath property for other resource
  imageInfo?: any;
  videoPlayUrl?: string;
  videoInfo?: {
    PosterUri: string;
    Vid: string;
    VideoMeta: {
      Bitrate?: number;
      Duration?: number;
      FileType?: string;
      Format?: string;
      Width?: number;
      Height?: number;
      Md5?: string;
      OriginWidth?: number;
      OriginHeight?: number;
      Size?: number;
      Uri?: string;
    };
  };
}

export interface MonitorParamsSchema {
  appName?: string;
  appId?: number;
  isInternal?: boolean;
}

export interface UploadConfigSchema extends MonitorParamsSchema {
  region?: string;
  onSuccess?: (
    result: SuccessDataSchema,
    resultJson?: SuccessDataSchema,
    fileType?: FileType,
    fileId?: string
  ) => void;
  onFail?: (e: Error, fileId?: string) => void;
  setProgress?: (progress: number) => void;
  isCSF?: boolean;
}

export enum UploadTypeEnum {
  IMAGE = 'image',
  VIDEO = 'video',
  OTHER = 'other'
}

export interface TosUploader {
  abort: () => any;
}

export const fileTypeMap = {
  image: imageType,
  video: videoType,
  pdf: pdfType,
  word: wordType,
  other: otherType
};

export const NewUploaderRegionsMap = {
  sg: 'ap-singapore-1',
  va: 'us-east-1',
  boei18n: 'boei18n'
};

export const NewUploaderImageHostMap = {
  sg: 'https://imagex-upload-sg.tiktok.com',
  va: 'https://imagex-upload-va.tiktok.com',
  'ap-singapore-1': 'https://imagex-upload-sg.tiktok.com',
  'us-east-1': 'https://imagex-upload-va.tiktok.com'
};

export const NewUploaderVideoHostMap = {
  sg: 'https://vod-upload-sg.tiktok.com',
  va: 'https://vod-upload-va.tiktok.com',
  'ap-singapore-1': 'https://vod-upload-sg.tiktok.com',
  'us-east-1': 'https://vod-upload-va.tiktok.com',
  boei18n: 'https://vas-boei18n.remove.this'
};
